/**
 * Hue Bridge Communication Service - API v2
 * Handles bridge authentication, light control, and API communication using Hue API v2
 * 
 * Key differences from v1:
 * - Uses HTTPS with /clip/v2 endpoint
 * - Authentication via 'hue-application-key' header instead of URL path
 * - UUIDs instead of numeric IDs
 * - Different payload structures
 */

import type { DiscoveredBridge } from './HueDiscoveryService'
import type { HueBridgeCredentials } from '../storage/SecureStorageInterface'
import { hueCredentialService } from '../storage/HueCredentialService'
import { convertToV2LightState, parseV1LightId, createZonePayload } from './HueV2StateConverter'
import type { LightState } from '@/services/realtime/LightPresenceService'
import { Platform } from '@/lib/platform'
import { apiLogger } from '@/lib/logger'
import { httpGet, httpPost, httpPut, type HttpResponse } from '@/lib/http'

// ==================== v2 API Types ====================

/**
 * v2 API Light Resource
 */
export interface HueV2Light {
  type: 'light'
  id: string // UUID
  id_v1?: string // e.g., "/lights/1" (temporary, will be removed)
  owner?: {
    rid: string
    rtype: 'device'
  }
  metadata: {
    name: string
    archetype?: string
  }
  on?: {
    on: boolean
  }
  dimming?: {
    brightness: number // 0-100
    min_dim_level?: number
  }
  color_temperature?: {
    mirek: number
    mirek_valid: boolean
    mirek_schema?: {
      mirek_minimum: number
      mirek_maximum: number
    }
  }
  color?: {
    xy: {
      x: number
      y: number
    }
    gamut?: {
      red: { x: number; y: number }
      green: { x: number; y: number }
      blue: { x: number; y: number }
    }
    gamut_type?: string
  }
  dynamics?: {
    status: string
    status_values: string[]
    speed: number
    speed_valid: boolean
  }
  alert?: {
    action_values: string[]
  }
  mode?: string
}

/**
 * v2 API Zone Resource
 */
export interface HueV2Zone {
  type: 'zone'
  id: string
  id_v1?: string
  metadata: {
    name: string
    archetype: string
  }
  children: Array<{
    rid: string
    rtype: 'light' | 'device'
  }>
  services: Array<{
    rid: string
    rtype: 'grouped_light'
  }>
}

/**
 * v2 API Grouped Light Resource (for controlling groups/zones)
 */
export interface HueV2GroupedLight {
  type: 'grouped_light'
  id: string
  id_v1?: string
  owner: {
    rid: string
    rtype: 'room' | 'zone'
  }
  on?: {
    on: boolean
  }
  dimming?: {
    brightness: number
  }
  alert?: {
    action_values: string[]
  }
}

/**
 * v2 API Response wrapper
 */
export interface HueV2Response<T> {
  errors: Array<{
    description: string
  }>
  data: T[]
}

/**
 * Resource Identifier (returned by POST operations)
 */
export interface ResourceIdentifier {
  rid: string // UUID
  rtype: 'device' | 'bridge_home' | 'room' | 'zone' | 'service_group' | 'light' | 'button' | 'grouped_light' | 'scene' | string
}

// ==================== Compatibility Types (kept for gradual migration) ====================

export interface HueLight {
  id: string
  name: string
  type: string
  modelid: string
  manufacturername: string
  productname: string
  capabilities: {
    certified: boolean
    streaming: {
      renderer: boolean
      proxy: boolean
    }
    control: {
      mindimlevel?: number
      maxlumen?: number
      colorgamuttype?: string
      colorgamut?: number[][]
      ct?: {
        min: number
        max: number
      }
    }
  }
  state: HueLightState
  config: {
    archetype: string
    function: string
    direction: string
    startup?: {
      mode: string
      configured: boolean
    }
  }
  uniqueid: string
  swversion: string
  swconfigid?: string
  productid?: string
}

export interface HueLightState {
  on: boolean
  bri?: number // 1-254
  hue?: number // 0-65535
  sat?: number // 0-254
  xy?: [number, number] // CIE color space
  ct?: number // Color temperature 153-500
  alert?: 'none' | 'select' | 'lselect'
  effect?: 'none' | 'colorloop'
  colormode?: 'hs' | 'xy' | 'ct'
  mode?: 'homeautomation'
  reachable?: boolean
}

export interface HueLightCommand {
  on?: boolean
  bri?: number
  hue?: number
  sat?: number
  xy?: [number, number]
  ct?: number
  alert?: 'none' | 'select' | 'lselect'
  effect?: 'none' | 'colorloop'
  transitiontime?: number // Transition time in 100ms units
}

export interface BridgeAuthenticationResult {
  success: boolean
  username?: string
  clientKey?: string
  error?: string
  needsLinkButton?: boolean
}

export interface BridgeInfo {
  name: string
  bridgeid: string
  modelid: string
  swversion: string
  apiversion: string
  mac: string
  factorynew: boolean
  replacesbridgeid?: string
  datastoreversion: string
  timezone: string
}

export interface HueGroup {
  id: string
  name: string
  type: 'Room' | 'Zone' | 'Entertainment' | 'LightGroup'
  class?: string // Room type like 'Living room', 'Kitchen', etc.
  lights: string[] // Array of light IDs
  state: {
    all_on: boolean
    any_on: boolean
  }
  action: HueLightState
}

export interface HueScene {
  id: string
  name: string
  type: string
  group?: string
  lights: string[]
  owner: string
  recycle: boolean
  locked: boolean
  appdata?: {
    version: number
    data: string
  }
  picture?: string
  lastupdated: string
}

export class HueBridgeService {
  private static readonly LINK_BUTTON_TIMEOUT = 30000 // 30 seconds
  private static readonly API_TIMEOUT = 10000 // 10 seconds

  /**
   * Make a v2 API request with proper headers and HTTPS
   * Handles self-signed certificate and CORS via Vite proxy in dev mode
   */
  private async makeV2Request<T>(
    ipAddress: string,
    applicationKey: string,
    endpoint: string,
    options: RequestInit = {}
  ): Promise<HueV2Response<T>> {
    // Build URL - uses native HTTP in Tauri, Vite proxy in dev web, direct HTTPS in production web
    const url = Platform.buildHueApiUrl(ipAddress, `/clip/v2${endpoint}`)
    
    const method = (options.method || 'GET') as 'GET' | 'POST' | 'PUT' | 'DELETE'
    apiLogger.debug(`[Hue v2 API] ${method} ${url}`)
    
    const headers = {
      'hue-application-key': applicationKey,
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {})
    }
    
    let response: HttpResponse<HueV2Response<T>>
    
    if (method === 'GET') {
      response = await httpGet<HueV2Response<T>>(url, {
        headers,
        timeout: HueBridgeService.API_TIMEOUT
      })
    } else if (method === 'POST') {
      response = await httpPost<HueV2Response<T>>(url, options.body as string, {
        headers,
        timeout: HueBridgeService.API_TIMEOUT
      })
    } else if (method === 'PUT') {
      response = await httpPut<HueV2Response<T>>(url, options.body as string, {
        headers,
        timeout: HueBridgeService.API_TIMEOUT
      })
    } else {
      throw new Error(`Unsupported HTTP method: ${method}`)
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText || ''}`)
    }

    return response.data
  }

  /**
   * Get application ID from /auth/v1 endpoint (required for Entertainment v2)
   */
  async getApplicationId(bridgeId: string, ipAddress: string): Promise<string | null> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        return null
      }

      const url = Platform.buildHueApiUrl(ipAddress, '/auth/v1')
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'hue-application-key': credentials.username
        },
        signal: AbortSignal.timeout(HueBridgeService.API_TIMEOUT)
      })

      if (!response.ok) {
        return null
      }

      // Application ID is returned in the response header
      const applicationId = response.headers.get('hue-application-id')
      
      if (applicationId && credentials) {
        // Store it in credentials
        credentials.applicationId = applicationId
        await hueCredentialService.storeBridgeCredentials(credentials)
      }

      return applicationId
    } catch (error) {
      apiLogger.error('Failed to get application ID:', error)
      return null
    }
  }

  /**
   * Fetch resource mappings from v2 API and store them
   * Maps v1 light IDs to v2 UUIDs using id_v1 field
   */
  async fetchResourceMappings(bridgeId: string, ipAddress: string): Promise<void> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found')
      }

      // Fetch all lights from v2 API
      const result = await this.makeV2Request<HueV2Light>(
        ipAddress,
        credentials.username,
        '/resource/light'
      )

      if (result.errors.length > 0) {
        apiLogger.error('v2 API errors:', result.errors)
        throw new Error(result.errors[0].description)
      }

      // Build resource ID map
      const resourceIdMap: { [v1Id: string]: string } = {}
      
      for (const light of result.data) {
        if (light.id_v1) {
          const v1Id = parseV1LightId(light.id_v1)
          if (v1Id) {
            resourceIdMap[v1Id] = light.id
          }
        }
      }

      // Store mappings in credentials
      credentials.resourceIdMap = resourceIdMap
      credentials.lastV2Sync = Date.now()
      await hueCredentialService.storeBridgeCredentials(credentials)

      apiLogger.info(`Fetched ${Object.keys(resourceIdMap).length} light resource mappings for bridge ${bridgeId}`)
    } catch (error) {
      apiLogger.error('Failed to fetch resource mappings:', error)
      throw error
    }
  }

  /**
   * Get v2 UUID for a v1 light ID
   * Automatically fetches mappings if not available
   */
  private async getV2LightId(bridgeId: string, ipAddress: string, v1LightId: string): Promise<string | null> {
    let credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
    if (!credentials) {
      return null
    }

    // Fetch mappings if not available
    if (!credentials.resourceIdMap) {
      apiLogger.warn('No resource mappings found, fetching now...')
      try {
        await this.fetchResourceMappings(bridgeId, ipAddress)
        // Re-fetch credentials after mapping update
        credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
        if (!credentials?.resourceIdMap) {
          apiLogger.error('Failed to fetch resource mappings')
          return null
        }
      } catch (error) {
        apiLogger.error('Error fetching resource mappings:', error)
        return null
      }
    }

    return credentials.resourceIdMap[v1LightId] || null
  }

  /**
   * Authenticate with a Hue bridge (link button flow)
   */
  async authenticateBridge(bridge: DiscoveredBridge): Promise<BridgeAuthenticationResult> {
    const deviceType = 'ambi_app#desktop'
    const generateClientKey = true

    try {
      // First, get bridge info to validate it's actually a Hue bridge (using v1 unauthenticated)
      const bridgeInfo = await this.getBridgeInfoV1(bridge.internalipaddress)
      if (!bridgeInfo) {
        return {
          success: false,
          error: 'Not a valid Hue bridge or bridge not reachable'
        }
      }

      const response = await httpPost<Array<Record<string, any>>>(
        `http://${bridge.internalipaddress}/api`,
        {
          devicetype: deviceType,
          generateclientkey: generateClientKey
        },
        {
          timeout: HueBridgeService.API_TIMEOUT
        }
      )

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText || ''}`
        }
      }

      const result = response.data
      
      if (Array.isArray(result) && result.length > 0) {
        const firstResult = result[0]

        if (firstResult.error) {
          if (firstResult.error.type === 101) {
            // Link button not pressed
            return {
              success: false,
              needsLinkButton: true,
              error: 'Press the link button on the bridge and try again'
            }
          } else {
            return {
              success: false,
              error: firstResult.error.description || 'Authentication failed'
            }
          }
        }

        if (firstResult.success) {
          const username = firstResult.success.username
          const clientKey = firstResult.success.clientkey

          // Store credentials locally
          const credentials: HueBridgeCredentials = {
            bridgeId: bridgeInfo.bridgeid,
            username,
            clientKey,
            lastConnected: Date.now(),
            bridgeInfo: {
              name: bridgeInfo.name,
              ip: bridge.internalipaddress,
              modelid: bridgeInfo.modelid,
              swversion: bridgeInfo.swversion
            }
          }

          await hueCredentialService.storeBridgeCredentials(credentials)

          // Fetch v2 resource mappings
          try {
            await this.fetchResourceMappings(bridgeInfo.bridgeid, bridge.internalipaddress)
            apiLogger.success('Successfully fetched v2 resource mappings during pairing')
          } catch (error) {
            apiLogger.warn('Failed to fetch v2 resource mappings during pairing:', error)
            // Non-fatal - can be fetched later
          }

          // Get application ID for Entertainment v2
          try {
            await this.getApplicationId(bridgeInfo.bridgeid, bridge.internalipaddress)
            apiLogger.success('Successfully fetched application ID during pairing')
          } catch (error) {
            apiLogger.warn('Failed to fetch application ID during pairing:', error)
            // Non-fatal
          }

          return {
            success: true,
            username,
            clientKey
          }
        }
      }

      return {
        success: false,
        error: 'Unexpected response format from bridge'
      }
    } catch (error) {
      apiLogger.error('Bridge authentication failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      }
    }
  }

  /**
   * Get bridge information using v2 API
   * Note: This requires credentials, so it's called after authentication
   */
  async getBridgeInfo(bridgeId: string, ipAddress: string): Promise<BridgeInfo | null> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        // Fallback to v1 unauthenticated endpoint for initial discovery
        return await this.getBridgeInfoV1(ipAddress)
      }

      const result = await this.makeV2Request<{
        id: string
        bridge_id: string
        time_zone: { time_zone: string }
        type: 'bridge'
      }>(
        ipAddress,
        credentials.username,
        '/resource/bridge'
      )

      if (!result || !result.data || result.data.length === 0 || (result.errors && result.errors.length > 0)) {
        // Fallback to v1
        return await this.getBridgeInfoV1(ipAddress)
      }

      const bridgeData = result.data[0]
      
      // Map v2 response to v1-compatible BridgeInfo format
      return {
        name: credentials.bridgeInfo?.name || 'Hue Bridge',
        bridgeid: bridgeData.bridge_id,
        modelid: credentials.bridgeInfo?.modelid || 'BSB002',
        swversion: credentials.bridgeInfo?.swversion || 'unknown',
        apiversion: '2.0',
        mac: bridgeData.bridge_id.substring(0, 12),
        factorynew: false,
        datastoreversion: 'v2',
        timezone: bridgeData.time_zone?.time_zone || 'UTC'
      }
    } catch (error) {
      apiLogger.error(`Failed to get bridge info for ${ipAddress}:`, error)
      // Fallback to v1
      return await this.getBridgeInfoV1(ipAddress)
    }
  }

  /**
   * Get bridge information using v1 API (unauthenticated, for initial discovery)
   * @private
   */
  private async getBridgeInfoV1(ipAddress: string): Promise<BridgeInfo | null> {
    try {
      const response = await httpGet<BridgeInfo>(
        `http://${ipAddress}/api/config`,
        {
          timeout: HueBridgeService.API_TIMEOUT
        }
      )

      if (!response.ok) {
        return null
      }

      const config = response.data
      
      // Validate it's a Hue bridge
      if (config.bridgeid && config.modelid) {
        return config as BridgeInfo
      }

      return null
    } catch (error) {
      apiLogger.error(`Failed to get v1 bridge info for ${ipAddress}:`, error)
      return null
    }
  }

  /**
   * Test bridge connection with stored credentials using v2 API
   */
  async testBridgeConnection(bridgeId: string, ipAddress: string): Promise<boolean> {
    apiLogger.debug(`Testing bridge ${bridgeId} at ${ipAddress}`)
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        apiLogger.debug(`No credentials found for bridge ${bridgeId}`)
        return false
      }

      apiLogger.debug(`Found credentials for bridge ${bridgeId}`)

      // Try v2 API endpoint
      const result = await this.makeV2Request<HueV2Light>(
        ipAddress,
        credentials.username,
        '/resource/light'
      )

      apiLogger.debug(`v2 API response:`, result)
      
      // If we got lights data, connection is good
      if (result.data && result.data.length >= 0) {
        apiLogger.success(`Connection successful for bridge ${bridgeId}`)
        await hueCredentialService.updateLastConnected(bridgeId)
        
        // Ensure resource mappings are up to date
        if (!credentials.resourceIdMap) {
          apiLogger.info('Fetching resource mappings during connection test...')
          await this.fetchResourceMappings(bridgeId, ipAddress)
        }
        
        return true
      }

      apiLogger.warn(`Unexpected response format for bridge ${bridgeId}`)
      return false
    } catch (error) {
      apiLogger.error(`Bridge connection test failed for ${bridgeId}:`, error)
      return false
    }
  }

  /**
   * Get all lights from a bridge using v2 API
   * Returns lights in v1-compatible format for gradual migration
   */
  async getAllLights(bridgeId: string, ipAddress: string): Promise<HueLight[]> {
    try {
      apiLogger.debug(`getAllLights called for bridge ${bridgeId} at ${ipAddress}`)
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found for bridge')
      }

      // Fetch resource mappings if not available
      if (!credentials.resourceIdMap) {
        apiLogger.info('No resource mappings found, fetching...')
        await this.fetchResourceMappings(bridgeId, ipAddress)
      }

      apiLogger.debug(`Fetching lights via v2 API for bridge ${bridgeId}...`)
      const result = await this.makeV2Request<HueV2Light>(
        ipAddress,
        credentials.username,
        '/resource/light'
      )

      if (result.errors.length > 0) {
        apiLogger.error('v2 API errors:', result.errors)
        throw new Error(result.errors[0].description)
      }

      apiLogger.info(`Received ${result.data.length} lights from v2 API`)

      // Convert v2 lights to v1-compatible format
      const lights: HueLight[] = result.data.map(v2Light => {
        const v1Id = v2Light.id_v1 ? parseV1LightId(v2Light.id_v1) : v2Light.id

        return {
          id: v1Id || v2Light.id,
          name: v2Light.metadata.name,
          type: v2Light.metadata.archetype || 'Unknown',
          modelid: 'unknown',
          manufacturername: 'Signify Netherlands B.V.',
          productname: v2Light.metadata.name,
          capabilities: {
            certified: true,
            streaming: {
              renderer: false,
              proxy: false
            },
            control: {
              colorgamuttype: v2Light.color?.gamut_type,
              colorgamut: v2Light.color?.gamut
                ? [
                    [v2Light.color.gamut.red.x, v2Light.color.gamut.red.y],
                    [v2Light.color.gamut.green.x, v2Light.color.gamut.green.y],
                    [v2Light.color.gamut.blue.x, v2Light.color.gamut.blue.y]
                  ]
                : undefined,
              ct: v2Light.color_temperature?.mirek_schema
                ? {
                    min: v2Light.color_temperature.mirek_schema.mirek_minimum,
                    max: v2Light.color_temperature.mirek_schema.mirek_maximum
                  }
                : undefined
            }
          },
          state: {
            on: v2Light.on?.on ?? false,
            bri: v2Light.dimming?.brightness
              ? Math.round((v2Light.dimming.brightness / 100) * 254)
              : undefined,
            xy: v2Light.color?.xy ? [v2Light.color.xy.x, v2Light.color.xy.y] : undefined,
            ct: v2Light.color_temperature?.mirek,
            colormode: v2Light.color?.xy ? 'xy' : v2Light.color_temperature?.mirek ? 'ct' : undefined,
            reachable: true // v2 doesn't have this concept
          },
          config: {
            archetype: v2Light.metadata.archetype || 'unknown',
            function: 'mixed',
            direction: 'omnidirectional'
          },
          uniqueid: v2Light.id,
          swversion: 'unknown'
        }
      })

      apiLogger.info(`Processed ${lights.length} lights from bridge ${bridgeId}`)
      return lights
    } catch (error) {
      apiLogger.error(`Failed to get lights from bridge ${bridgeId}:`, error)
      throw error
    }
  }

  /**
   * Get all groups (rooms and zones) from a bridge using v2 API
   */
  async getAllGroups(bridgeId: string, ipAddress: string): Promise<HueGroup[]> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found for bridge')
      }

      // Fetch both rooms and zones
      const [roomsResult, zonesResult] = await Promise.all([
        this.makeV2Request<{
          id: string
          id_v1?: string
          type: 'room'
          metadata: { name: string; archetype?: string }
          children?: Array<{ rid: string; rtype: string }>
          services?: Array<{ rid: string; rtype: string }>
        }>(ipAddress, credentials.username, '/resource/room'),
        
        this.makeV2Request<HueV2Zone>(ipAddress, credentials.username, '/resource/zone')
      ])

      const groups: HueGroup[] = []

      // Convert rooms to v1-compatible groups
      for (const room of roomsResult.data) {
        const lights = room.children
          ?.filter(child => child.rtype === 'light')
          .map(child => child.rid) || []

        const groupedLight = room.services?.find(s => s.rtype === 'grouped_light')

        groups.push({
          id: room.id,
          name: room.metadata.name,
          type: 'Room',
          class: room.metadata.archetype,
          lights: lights,
          state: {
            all_on: false, // Would need to query grouped_light for actual state
            any_on: false
          },
          action: {
            on: false,
            bri: 254
          }
        })
      }

      // Convert zones to v1-compatible groups
      for (const zone of zonesResult.data) {
        const lights = zone.children
          ?.filter(child => child.rtype === 'light')
          .map(child => child.rid) || []

        groups.push({
          id: zone.id,
          name: zone.metadata.name,
          type: 'Zone',
          class: zone.metadata.archetype,
          lights: lights,
          state: {
            all_on: false,
            any_on: false
          },
          action: {
            on: false,
            bri: 254
          }
        })
      }

      return groups
    } catch (error) {
      apiLogger.error(`Failed to get groups from bridge ${bridgeId}:`, error)
      throw error
    }
  }

  /**
   * Get all scenes from a bridge using v2 API
   */
  async getAllScenes(bridgeId: string, ipAddress: string): Promise<HueScene[]> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found for bridge')
      }

      const result = await this.makeV2Request<{
        id: string
        id_v1?: string
        type: 'scene'
        metadata: {
          name: string
          image?: { rid: string; rtype: string }
          appdata?: string
        }
        group: {
          rid: string
          rtype: string
        }
        actions: Array<{
          target: { rid: string; rtype: string }
          action: Record<string, unknown>
        }>
        palette?: Record<string, unknown>
        speed: number
        auto_dynamic: boolean
        status?: {
          active?: 'inactive' | 'static' | 'dynamic_palette'
        }
      }>(
        ipAddress,
        credentials.username,
        '/resource/scene'
      )

      if (result.errors.length > 0) {
        apiLogger.error('v2 API errors:', result.errors)
        throw new Error(result.errors[0].description)
      }

      // Convert v2 scenes to v1-compatible format
      const scenes: HueScene[] = result.data.map(v2Scene => {
        const v1Id = v2Scene.id_v1 ? v2Scene.id_v1.split('/').pop() : v2Scene.id

        // Extract light IDs from actions
        const lights = v2Scene.actions
          .map(action => action.target.rid)
          .filter(Boolean)

        return {
          id: v1Id || v2Scene.id,
          name: v2Scene.metadata.name,
          type: 'LightScene',
          group: v2Scene.group.rid, // v2 group UUID
          lights: lights,
          owner: 'api',
          recycle: false,
          locked: false,
          appdata: v2Scene.metadata.appdata ? {
            version: 1,
            data: v2Scene.metadata.appdata
          } : undefined,
          picture: v2Scene.metadata.image?.rid,
          lastupdated: new Date().toISOString()
        }
      })

      return scenes
    } catch (error) {
      apiLogger.error(`Failed to get scenes from bridge ${bridgeId}:`, error)
      throw error
    }
  }

  /**
   * Control a light using v2 API
   * @param lightId v1 light ID (will be mapped to v2 UUID internally)
   * @param state Internal light state
   * @param transitionTime Transition time in deciseconds (v1 format)
   */
  async setLightState(
    bridgeId: string,
    ipAddress: string,
    lightId: string,
    state: Partial<LightState>,
    transitionTime: number = 4
  ): Promise<boolean> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found for bridge')
      }

      // Get v2 UUID for this light (will fetch mappings if needed)
      const v2LightId = await this.getV2LightId(bridgeId, ipAddress, lightId)
      if (!v2LightId) {
        throw new Error(`No v2 mapping found for light ${lightId}`)
      }

      // Convert to v2 payload
      const v2Payload = convertToV2LightState(state, transitionTime)

      // Make v2 API request
      const result = await this.makeV2Request<HueV2Light>(
        ipAddress,
        credentials.username,
        `/resource/light/${v2LightId}`,
        {
          method: 'PUT',
          body: JSON.stringify(v2Payload)
        }
      )

      // v2 API returns warnings as errors - only throw on critical errors
      if (result.errors.length > 0) {
        const criticalErrors = result.errors.filter(err => 
          !err.description.includes('communication issues') &&
          !err.description.includes('may not have effect')
        )
        
        if (criticalErrors.length > 0) {
          apiLogger.error('v2 API critical errors:', criticalErrors)
          throw new Error(criticalErrors[0].description)
        } else {
          // Just warnings about unreachable lights
          apiLogger.warn(`⚠️ Light ${lightId} may be unreachable:`, result.errors.map(e => e.description))
        }
      }

      return true
    } catch (error) {
      apiLogger.error(`Failed to set light ${lightId} state on bridge ${bridgeId}:`, error)
      throw error
    }
  }

  /**
   * Legacy method signature for compatibility (converts HueLightCommand to LightState)
   * @deprecated Use setLightState with LightState instead
   */
  async setLightStateV1Command(bridgeId: string, ipAddress: string, lightId: string, command: HueLightCommand): Promise<boolean> {
    const state: Partial<LightState> = {
      on: command.on,
      brightness: command.bri,
      hue: command.hue,
      saturation: command.sat,
      xy: command.xy,
      ct: command.ct
    }
    return this.setLightState(bridgeId, ipAddress, lightId, state, command.transitiontime)
  }

  /**
   * Get all rooms from bridge (v2 API)
   * Rooms are persistent groups managed by the bridge
   */
  async getAllRooms(bridgeId: string, ipAddress: string): Promise<Array<{
    id: string
    name: string
    lights: string[] // v2 light UUIDs
    groupedLightId: string // For controlling all lights at once
  }>> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found')
      }

      const result = await this.makeV2Request<HueV2Zone>(
        ipAddress,
        credentials.username,
        '/resource/room'
      )

      if (result.errors.length > 0) {
        apiLogger.error('Failed to fetch rooms:', result.errors)
        return []
      }

      return result.data.map(room => ({
        id: room.id,
        name: room.metadata.name,
        lights: room.children
          ?.filter(child => child.rtype === 'light')
          .map(child => child.rid) || [],
        groupedLightId: room.services?.find(s => s.rtype === 'grouped_light')?.rid || ''
      }))
    } catch (error) {
      apiLogger.error('Failed to get rooms:', error)
      return []
    }
  }

  /**
   * Control a room via its grouped_light service
   * More efficient than creating temporary zones
   */
  async setRoomState(
    bridgeId: string,
    ipAddress: string,
    groupedLightId: string,
    state: Partial<LightState>,
    transitionTime: number = 4
  ): Promise<boolean> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found')
      }

      const v2Payload = convertToV2LightState(state, transitionTime)
      
      const result = await this.makeV2Request<HueV2GroupedLight>(
        ipAddress,
        credentials.username,
        `/resource/grouped_light/${groupedLightId}`,
        {
          method: 'PUT',
          body: JSON.stringify(v2Payload)
        }
      )

      // Handle warnings vs critical errors
      if (result.errors.length > 0) {
        const criticalErrors = result.errors.filter(err => 
          !err.description.includes('communication issues') &&
          !err.description.includes('may not have effect')
        )
        
        if (criticalErrors.length > 0) {
          apiLogger.error('Failed to control room:', criticalErrors)
          return false
        } else {
          apiLogger.warn(`⚠️ Some lights in group may be unreachable:`, result.errors.map(e => e.description))
        }
      }

      return true
    } catch (error) {
      apiLogger.error('Failed to set room state:', error)
      return false
    }
  }



  /**
   * Start EventStream for real-time updates (Hue API v2)
   * Note: This requires the bridge to support v2 API and HTTPS
   */
  async startEventStream(bridgeId: string, ipAddress: string): Promise<EventSource | null> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials || !credentials.clientKey) {
        apiLogger.warn('EventStream requires client key from v2 authentication')
        return null
      }

      // Check if bridge supports HTTPS (required for EventStream)
      const eventStreamUrl = `https://${ipAddress}/eventstream/clip/v2`
      
      const eventSource = new EventSource(eventStreamUrl, {
        // Note: In a real implementation, you'd need to handle authentication headers
        // This is a simplified version
      })

      eventSource.onerror = (error) => {
        apiLogger.error('EventStream error:', error)
      }

      return eventSource
    } catch (error) {
      apiLogger.error('Failed to start EventStream:', error)
      return null
    }
  }

  /**
   * Get all zones from a bridge
   */
  async getAllZones(bridgeId: string, ipAddress: string): Promise<HueV2Zone[]> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found for bridge')
      }

      const response = await this.makeV2Request<HueV2Zone>(
        ipAddress,
        credentials.username,
        '/resource/zone'
      )

      return response.data || []
    } catch (error) {
      apiLogger.error('Failed to get zones:', error)
      return []
    }
  }

  /**
   * Delete a zone by ID
   */
  async deleteZone(bridgeId: string, ipAddress: string, zoneId: string): Promise<boolean> {
    try {
      const credentials = await hueCredentialService.getBridgeCredentials(bridgeId)
      if (!credentials) {
        throw new Error('No credentials found for bridge')
      }

      await this.makeV2Request(
        ipAddress,
        credentials.username,
        `/resource/zone/${zoneId}`,
        {
          method: 'DELETE'
        }
      )

      return true
    } catch (error) {
      apiLogger.error(`Failed to delete zone ${zoneId}:`, error)
      return false
    }
  }

  /**
   * Sync all lights from bridge to database
   * Discovers lights and their room assignments, then saves them to the lights table
   */
  async syncAllLightsFromBridge(bridgeId: string, ipAddress: string): Promise<void> {
    apiLogger.info(`🔄 Syncing all lights from bridge ${bridgeId}`)

    try {
      // Get user ID from auth store
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()
      if (!authStore.user) {
        apiLogger.error('No authenticated user for light sync')
        throw new Error('No authenticated user')
      }

      // Fetch both lights and groups to get room assignments
      const [lights, groups] = await Promise.all([
        this.getAllLights(bridgeId, ipAddress),
        this.getAllGroups(bridgeId, ipAddress)
      ])
      
      apiLogger.info(`Fetched ${lights.length} lights and ${groups.length} groups from bridge ${bridgeId}`)

      // Create a mapping of light ID to room name
      const lightToRoomMap = new Map<string, string>()
      
      // Process groups (rooms/zones) to map lights to rooms
      for (const group of groups) {
        // Only process Room type groups, skip zones and other types
        if (group.type === 'Room' && group.lights) {
          for (const lightId of group.lights) {
            lightToRoomMap.set(lightId, group.name)
          }
        }
      }

      apiLogger.info(`Created room mapping for ${lightToRoomMap.size} lights`)

      // Use DataProvider for database operations (works in both online and standalone mode)
      const { getDataProvider, TABLES } = await import('@/lib/dataProvider')
      const provider = getDataProvider()

      // Sync each light to database
      for (const light of lights) {
        const roomName = lightToRoomMap.get(light.id) || null
        
        try {
          // Use upsert to handle both new and existing lights
          // onConflict: composite key to identify unique lights
          const result = await provider.upsert(TABLES.LIGHTS, {
            user_id: authStore.user.id,
            hue_bridge_id: bridgeId,
            hue_light_id: light.id,
            name: light.name,
            room_name: roomName,
            light_type: light.type,
            capabilities: light.capabilities,
            current_state: light.state,
            is_available: light.state.reachable ?? true,
            last_seen: new Date().toISOString()
          }, 'user_id,hue_bridge_id,hue_light_id')

          if (!result.success) {
            apiLogger.error(`Failed to sync light ${light.id} to database:`, result.error)
          }
        } catch (error) {
          apiLogger.error(`Failed to sync light ${light.id} from bridge:`, error)
        }
      }

      apiLogger.success(`✅ Successfully synced ${lights.length} lights from bridge ${bridgeId}`)
    } catch (error) {
      apiLogger.error(`Failed to sync all lights from bridge ${bridgeId}:`, error)
      throw error
    }
  }

  /**
   * Sync scenes from bridge (for room-based scene organization)
   * Returns room-specific scenes (scenes with a group property)
   */
  async syncScenesFromBridge(bridgeId: string, ipAddress: string): Promise<HueScene[]> {
    try {
      const scenes = await this.getAllScenes(bridgeId, ipAddress)
      apiLogger.info(`Fetched ${scenes.length} scenes from bridge ${bridgeId}`)
      
      // Filter and return room-specific scenes (scenes with a group property)
      return scenes.filter(scene => scene.group && scene.group !== '0')
    } catch (error) {
      apiLogger.error(`Failed to sync scenes from bridge ${bridgeId}:`, error)
      return []
    }
  }
}

// Singleton instance
export const hueBridgeService = new HueBridgeService()